import { UseFlashSaleInterface, CampaignConfig } from "./useFlashSale";
import api from "~/config/extensionsApi";
import { useConfigStore, useFlashSaleStore } from "~/stores/extensionApi";
import { computed, ref } from "@nuxtjs/composition-api";
import { getApiAxios } from "~/helpers";
import { mapProduct } from "~/modules/catalog/category/helpers/mapProduct";

export function useFlashSale(): UseFlashSaleInterface {
  const loading = ref<boolean>(false);
  const flashSale = ref<any[]>([]);
  const flashSaleProduct = ref<any>(null);
  const campaignConfig = ref<CampaignConfig | null>(null);
  const campaignConfigLoading = ref<boolean>(false);
  const flashSaleStore = useFlashSaleStore();
  const storeConfig = useConfigStore();
  const countdownImage = computed(() => storeConfig.getCountdownImage);
  const flashDeal = computed(() => storeConfig.getFlashDealConfig);

  async function load({ pageSize = 16, offset = 0 } = {}): Promise<any[]> {
    try {
      loading.value = true;
      const response = await getApiAxios(api.flashsale.flashsale_deals + `?pageSize=${pageSize}&offset=${offset}`);
      if (response && response.code == 200) {
        const results = response.result || [];
        flashSaleStore.flashSale = results;

        flashSale.value = results.length ? results.map((value) => mapProduct(value)) : [];
        return results.length ? results.map((value) => mapProduct(value)) : [];
      }
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    } finally {
      loading.value = false;
    }
  }

  async function getFlashSaleProductBySku(sku: string): Promise<void> {
    try {
      loading.value = true;
      if (flashDeal.value && Object.keys(flashDeal.value).length> 0) {
      const response = await getApiAxios(`${api.flashsale.flashsale_product}?sku=${sku}`);
      flashSaleProduct.value = response?.result;
      }
    } catch (error) {
      console.error("Error fetching flashsale product:", error);
    } finally {
      loading.value = false;
    }
  }

  async function getCampaignConfig(): Promise<void> {
    try {
      campaignConfigLoading.value = true;
      if (countdownImage.value && Object.keys(countdownImage.value).length> 0) {
        const response = await getApiAxios(api.config.config_campaign);
        campaignConfig.value = response?.result;
      }
    } catch (error) {
      console.error("Error fetching campaign config:", error);
      campaignConfig.value = null;
    } finally {
      campaignConfigLoading.value = false;
    }
  }

  function isCampaignActive(): boolean {
    // Check if campaign config exists and has a valid countdown time
    if (!campaignConfig.value || !campaignConfig.value.campaign) return false;

    // If countdown_time exists and is greater than 0, the campaign is active
    const countdownTime = campaignConfig.value.campaign.countdown_time;
    return typeof countdownTime === "number" && countdownTime > 0;
  }

  function getCampaignDuration(): { startDate: Date | null; endDate: Date | null } {
    return {
      startDate: campaignConfig.value?.start_date ? new Date(campaignConfig.value.start_date) : null,
      endDate: campaignConfig.value?.end_date ? new Date(campaignConfig.value.end_date) : null,
    };
  }

  function getCountdownTimeInSeconds(): number {
    // Get countdown time in seconds, default to 0 if not available
    return Math.floor((campaignConfig.value?.campaign?.countdown_time || 0) / 1000);
  }

  function getCountdownDate(): Date | null {
    const countdownTimeMs = campaignConfig.value?.campaign?.countdown_time;
    if (!countdownTimeMs) return null;

    // Calculate end date by adding countdown time to current date
    const now = new Date();
    return new Date(now.getTime() + countdownTimeMs);
  }

  function isCountdownActive(): boolean {
    return (campaignConfig.value?.campaign?.countdown_time || 0) > 0;
  }

  return {
    loading,
    load,
    flashSale,
    flashSaleProduct,
    getFlashSaleProductBySku,
    campaignConfig,
    campaignConfigLoading,
    getCampaignConfig,
    isCampaignActive,
    getCampaignDuration,
    getCountdownTimeInSeconds,
    getCountdownDate,
    isCountdownActive,
  };
}
